import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import DevicesTable from "./DevicesTable";
import { devicesData, locationsData } from "../../utils/constants";

export default function DevicesPage() {
  const [devices] = useState(devicesData);
  const [sortBy, setSortBy] = useState("model");
  const navigate = useNavigate();
  
  // Extract all individual device instances from locations
  const mfrDevices = [];
  const sapFlowDevices = [];
  const bandDendrometerDevices = [];
  const wxt536Devices = [];
  const aqt530Devices = [];
  
  locationsData.forEach(location => {
    location.devices.forEach(device => {
      if (device.type === "MFR Node") {
        mfrDevices.push({
          ...device,
          locationName: location.name,
          locationFullName: location.fullName
        });
      } else if (device.type === "Sap Flow Sensor") {
        sapFlowDevices.push({
          ...device,
          locationName: location.name,
          locationFullName: location.fullName
        });
      } else if (device.type === "Band Dendrometer") {
        bandDendrometerDevices.push({
          ...device,
          locationName: location.name,
          locationFullName: location.fullName
        });
      } else if (device.type === "WXT536 Sensor") {
        wxt536Devices.push({
          ...device,
          locationName: location.name,
          locationFullName: location.fullName
        });
      } else if (device.type === "AQT530 Sensor") {
        aqt530Devices.push({
          ...device,
          locationName: location.name,
          locationFullName: location.fullName
        });
      }
    });
  });
  
  return (
    <div className="max-w-7xl mx-auto px-4 py-4">
      <h1 className="text-2xl font-semibold mb-4">Devices & Sensors</h1>
      
      {/* Devices Section */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-3">Devices</h2>
        <DevicesTable 
          devices={devices} 
          onDeviceClick={(device) => navigate(`/device/${device.model}`)}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />
      </div>
      
      {/* MFR Nodes Section */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-3">MFR Nodes ({mfrDevices.length})</h2>
        <div className="border rounded-lg overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left">Serial Number</th>
                <th className="px-3 py-3 text-left">Location</th>
                <th className="px-3 py-3 text-left">Site</th>
                <th className="px-3 py-3 text-left">Attached Sensors</th>
              </tr>
            </thead>
            <tbody>
              {mfrDevices.map((device, idx) => (
                <tr 
                  key={idx} 
                  className="border-b hover:bg-gray-50"
                >
                  <td className="px-3 py-3">
                    <a 
                      href={`/device/${device.serialNumber}`}
                      onClick={(e) => {
                        if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
                          e.preventDefault();
                          navigate(`/device/${device.serialNumber}`, { state: { device } });
                        }
                      }}
                      className="text-blue-600 hover:underline cursor-pointer"
                    >
                      {device.serialNumber}
                    </a>
                  </td>
                  <td className="px-3 py-3">{device.locationFullName} ({device.locationName})</td>
                  <td className="px-3 py-3">{device.site || "-"}</td>
                  <td className="px-3 py-3">{device.attachedSensors?.join(', ') || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Sap Flow Sensors Section */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-3">Sap Flow Sensors ({sapFlowDevices.length})</h2>
        <div className="border rounded-lg overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left">Serial Number</th>
                <th className="px-3 py-3 text-left">Location</th>
                <th className="px-3 py-3 text-left">Tree Species</th>
                <th className="px-3 py-3 text-left">DBH</th>
              </tr>
            </thead>
            <tbody>
              {sapFlowDevices.map((device, idx) => (
                <tr 
                  key={idx} 
                  className="border-b hover:bg-gray-50"
                >
                  <td className="px-3 py-3">
                    <a 
                      href={`/device/${device.serialNumber}`}
                      onClick={(e) => {
                        if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
                          e.preventDefault();
                          navigate(`/device/${device.serialNumber}`, { state: { device } });
                        }
                      }}
                      className="text-blue-600 hover:underline cursor-pointer"
                    >
                      {device.serialNumber}
                    </a>
                  </td>
                  <td className="px-3 py-3">{device.locationFullName} ({device.locationName})</td>
                  <td className="px-3 py-3">{device.treeSpecies || "-"}</td>
                  <td className="px-3 py-3">{device.treeDiameter || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Band Dendrometers Section */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-3">Band Dendrometers ({bandDendrometerDevices.length})</h2>
        <div className="border rounded-lg overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left">Serial Number</th>
                <th className="px-3 py-3 text-left">Location</th>
                <th className="px-3 py-3 text-left">Tree Species</th>
                <th className="px-3 py-3 text-left">DBH</th>
              </tr>
            </thead>
            <tbody>
              {bandDendrometerDevices.map((device, idx) => (
                <tr 
                  key={idx} 
                  className="border-b hover:bg-gray-50"
                >
                  <td className="px-3 py-3">
                    <a 
                      href={`/device/${device.serialNumber}`}
                      onClick={(e) => {
                        if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
                          e.preventDefault();
                          navigate(`/device/${device.serialNumber}`, { state: { device } });
                        }
                      }}
                      className="text-blue-600 hover:underline cursor-pointer"
                    >
                      {device.serialNumber}
                    </a>
                  </td>
                  <td className="px-3 py-3">{device.locationFullName} ({device.locationName})</td>
                  <td className="px-3 py-3">{device.treeSpecies || "-"}</td>
                  <td className="px-3 py-3">{device.treeDiameter || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* WXT536 Sensors Section */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-3">WXT536 Weather Sensors ({wxt536Devices.length})</h2>
        <div className="border rounded-lg overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left">Device ID</th>
                <th className="px-3 py-3 text-left">Location</th>
                <th className="px-3 py-3 text-left">Site</th>
              </tr>
            </thead>
            <tbody>
              {wxt536Devices.map((device, idx) => (
                <tr 
                  key={idx} 
                  className="border-b hover:bg-gray-50"
                >
                  <td className="px-3 py-3">
                    <a 
                      href={`/device/${device.serialNumber}`}
                      onClick={(e) => {
                        if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
                          e.preventDefault();
                          navigate(`/device/${device.serialNumber}`, { state: { device } });
                        }
                      }}
                      className="text-blue-600 hover:underline cursor-pointer"
                    >
                      {device.serialNumber}
                    </a>
                  </td>
                  <td className="px-3 py-3">{device.locationFullName} ({device.locationName})</td>
                  <td className="px-3 py-3">{device.site || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* AQT530 Sensors Section */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-3">AQT530 Air Quality Sensors ({aqt530Devices.length})</h2>
        <div className="border rounded-lg overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left">Device ID</th>
                <th className="px-3 py-3 text-left">Location</th>
                <th className="px-3 py-3 text-left">Site</th>
              </tr>
            </thead>
            <tbody>
              {aqt530Devices.map((device, idx) => (
                <tr 
                  key={idx} 
                  className="border-b hover:bg-gray-50"
                >
                  <td className="px-3 py-3">
                    <a 
                      href={`/device/${device.serialNumber}`}
                      onClick={(e) => {
                        if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
                          e.preventDefault();
                          navigate(`/device/${device.serialNumber}`, { state: { device } });
                        }
                      }}
                      className="text-blue-600 hover:underline cursor-pointer"
                    >
                      {device.serialNumber}
                    </a>
                  </td>
                  <td className="px-3 py-3">{device.locationFullName} ({device.locationName})</td>
                  <td className="px-3 py-3">{device.site || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}